/**
 * KGS Candidate Portal — full 3-column redesign (Feb 2026).
 * Matches the client's reference brief: left brand-navy sidebar, top bar with
 * notification bell + user menu, welcome hero, profile-completion progress,
 * 4 status cards, dossier section list, and right-hand widget rail with
 * Recent Notifications, My Documents, and Quick Links.
 */
import { useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import PortalDossier from "@/components/PortalDossier";
import PortalChangeRequestForm from "@/components/PortalChangeRequestForm";
import PortalTimeline from "@/components/PortalTimeline";
import PortalNotifications from "@/components/PortalNotifications";
import PortalAttendance from "@/components/PortalAttendance";
import {
  House, IdentificationCard, FileText, ClipboardText, Briefcase, PencilSimpleLine,
  ChatCircle, Bell, ClockCounterClockwise, Lifebuoy, Gear, SignOut, CalendarCheck,
  List, CaretDown, PaperPlaneTilt, CheckCircle, Clock, Info, ArrowRight,
  FilePdf, User, GraduationCap, Sparkle, ShieldCheck, MapPin, Trophy,
  CalendarBlank, Flag, Buildings, PlusCircle, Headset,
} from "@phosphor-icons/react";

const LOGO_HORIZONTAL = "/kgs-logo-horizontal.png";
const LOGO_ICON = "/kgs-logo-icon.png";

// ── Navigation config ─────────────────────────────────────────────
const NAV = [
  { key: "dashboard", label: "Dashboard", icon: House },
  { key: "dossier", label: "My Dossier", icon: IdentificationCard },
  { key: "attendance", label: "Interview Attendance", icon: CalendarCheck, badge: "attendance" },
  { key: "documents", label: "Documents", icon: FileText },
  { key: "evaluations", label: "Evaluations", icon: ClipboardText },
  { key: "applications", label: "My Applications", icon: Briefcase },
  { key: "change-requests", label: "Change Requests", icon: PencilSimpleLine },
  { key: "messages", label: "Messages", icon: ChatCircle, badge: "complaints" },
  { key: "notifications", label: "Notifications", icon: Bell, badge: "notif" },
  { key: "timeline", label: "Timeline", icon: ClockCounterClockwise },
  { key: "support", label: "Support", icon: Lifebuoy },
  { key: "settings", label: "Profile Settings", icon: Gear },
];

const TAB_TITLES = {
  dashboard: "Candidate Portal", dossier: "My Dossier",
  attendance: "Interview Attendance",
  documents: "Documents",
  evaluations: "Evaluations", applications: "My Applications",
  "change-requests": "Change Requests", messages: "Messages",
  notifications: "Notifications", timeline: "Timeline",
  support: "Support", settings: "Profile Settings",
};

// ── Root ─────────────────────────────────────────────────────────
export default function CandidatePortal() {
  const nav = useNavigate();
  const [token, setToken] = useState(sessionStorage.getItem("portal_token") || "");
  const [creds, setCreds] = useState({ email: "", password: "" });
  const [me, setMe] = useState(null);
  const [subs, setSubs] = useState([]);
  const [ivs, setIvs] = useState([]);
  const [ofs, setOfs] = useState([]);
  const [complaints, setComplaints] = useState([]);
  const [tab, setTab] = useState("dashboard");
  const [dashboard, setDashboard] = useState(null);
  const [notifs, setNotifs] = useState([]);
  const [notifUnread, setNotifUnread] = useState(0);
  const [docs, setDocs] = useState([]);
  const [evals, setEvals] = useState([]);
  const [changeRequests, setChangeRequests] = useState([]);
  const [applications, setApplications] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const authFetch = useCallback(async (url, opts = {}) => {
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}${url}`, {
      ...opts, headers: { ...(opts.headers || {}), Authorization: `Bearer ${token}` },
    });
    if (res.status === 401) { logout(); throw new Error("Session expired"); }
    return res;
  }, [token]);   // eslint-disable-line react-hooks/exhaustive-deps

  const login = async (e) => {
    e.preventDefault();
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/portal/login`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(creds),
      });
      if (!r.ok) throw new Error("Invalid credentials");
      const d = await r.json();
      sessionStorage.setItem("portal_token", d.access_token);
      setToken(d.access_token);
      toast.success("Welcome back!");
    } catch (ex) { toast.error(ex.message); }
  };
  const logout = () => { sessionStorage.removeItem("portal_token"); setToken(""); setMe(null); };

  const load = useCallback(async () => {
    if (!token) return;
    try {
      const [m, s, i, o, c, n, dash, dc, ev, cr, ap] = await Promise.all([
        authFetch("/api/portal/me").then((r) => r.json()),
        authFetch("/api/portal/submissions").then((r) => r.json()).catch(() => []),
        authFetch("/api/portal/interviews").then((r) => r.json()).catch(() => []),
        authFetch("/api/portal/offers").then((r) => r.json()).catch(() => []),
        authFetch("/api/portal/complaints").then((r) => r.json()).catch(() => []),
        authFetch("/api/portal/notifications").then((r) => r.json()).catch(() => ({ rows: [], unread: 0 })),
        authFetch("/api/portal/dashboard").then((r) => r.json()).catch(() => null),
        authFetch("/api/portal/documents").then((r) => r.json()).catch(() => ({ rows: [] })),
        authFetch("/api/portal/evaluations").then((r) => r.json()).catch(() => ({ rows: [] })),
        authFetch("/api/portal/change-requests").then((r) => r.json()).catch(() => ({ rows: [] })),
        authFetch("/api/portal/applications").then((r) => r.json()).catch(() => ({ rows: [] })),
      ]);
      setMe(m); setSubs(s || []); setIvs(i || []); setOfs(o || []); setComplaints(c || []);
      setNotifs(n?.rows || []); setNotifUnread(n?.unread || 0); setDashboard(dash);
      setDocs(dc?.rows || []); setEvals(ev?.rows || []); setChangeRequests(cr?.rows || []);
      setApplications(ap?.rows || []);
    } catch (ex) { toast.error(ex.message); }
  }, [token, authFetch]);
  useEffect(() => { load(); }, [load]);

  if (!token) {
    // UAT (Feb 2026) — the portal no longer shows an inline login card. All
    // unauthenticated visitors are redirected to the unified branded entry.
    nav("/login?tab=candidate", { replace: true });
    return <div className="min-h-screen grid place-items-center text-slate-400 text-sm">Redirecting to sign-in…</div>;
  }
  if (!me) return <div className="min-h-screen grid place-items-center text-slate-400 text-sm">Loading portal…</div>;

  const firstName = me.full_name?.split(" ")[0] || "there";
  const initials = (me.full_name || "").split(" ").map((s) => s[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
  const complaintsBadge = complaints.filter((c) => c.status !== "Resolved").length;

  return (
    <div className="min-h-screen bg-[#F5F7FA] flex">
      <Sidebar tab={tab} setTab={setTab} logout={logout}
               notifBadge={notifUnread} complaintsBadge={complaintsBadge}
               open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <div className="flex-1 flex flex-col min-w-0">
        <TopBar title={TAB_TITLES[tab] || "Candidate Portal"}
                me={me} initials={initials} notifUnread={notifUnread}
                onNotifClick={() => setTab("notifications")}
                onMenu={() => setSidebarOpen(true)}
                onLogout={logout} />

        <div className="flex-1 flex overflow-hidden">
          <main className="flex-1 overflow-y-auto min-w-0" data-testid="portal-main">
            <div className="p-6 md:p-8 space-y-6">
              {tab === "dashboard" && (
                <DashboardHome me={me} firstName={firstName} dashboard={dashboard}
                                onNav={setTab} ivs={ivs} subs={subs} />
              )}
              {tab === "dossier" && <PortalDossier authFetch={authFetch} me={me} onSaved={load} />}
              {tab === "attendance" && <PortalAttendance />}
              {tab === "documents" && <DocumentsView docs={docs} />}
              {tab === "evaluations" && <EvaluationsView evals={evals} />}
              {tab === "applications" && <ApplicationsView applications={applications} authFetch={authFetch} />}
              {tab === "change-requests" && (
                <ChangeRequestsView authFetch={authFetch} me={me} changeRequests={changeRequests} onRefresh={load} />
              )}
              {tab === "messages" && <MessagesPlaceholder />}
              {tab === "notifications" && (
                <PortalNotifications authFetch={authFetch} unreadHint={notifUnread} onRefresh={load} />
              )}
              {tab === "timeline" && <PortalTimeline authFetch={authFetch} />}
              {tab === "support" && <SupportPanel authFetch={authFetch} complaints={complaints} onRefresh={load} />}
              {tab === "settings" && <SettingsView me={me} onLogout={logout} />}
            </div>
          </main>

          {tab === "dashboard" && (
            <aside className="hidden xl:block w-[340px] shrink-0 border-l border-slate-200 bg-white overflow-y-auto"
                   data-testid="portal-right-rail">
              <RightRail notifs={notifs} docs={docs} onNav={setTab} />
            </aside>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Sidebar ───────────────────────────────────────────────────────
function Sidebar({ tab, setTab, logout, notifBadge, complaintsBadge, open, onClose }) {
  return (
    <>
      {open && <div className="lg:hidden fixed inset-0 bg-slate-900/40 z-40" onClick={onClose} />}
      <aside data-testid="portal-sidebar" className={`
        ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
        fixed lg:static inset-y-0 left-0 z-50 w-[260px] bg-white border-r border-slate-200
        transition-transform duration-200 flex flex-col shrink-0
      `}>
        <div className="px-5 py-5 border-b border-slate-100 flex items-center gap-3">
          <img src={LOGO_ICON} alt="KGS" className="h-9 w-auto" />
          <div>
            <div className="text-[15px] font-bold text-slate-900 leading-tight">KGS</div>
            <div className="text-[9px] tracking-[0.18em] text-slate-500 font-medium">TECHNOLOGY GROUP</div>
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-0.5">
          {NAV.map((item) => {
            const Icon = item.icon;
            const active = tab === item.key;
            const badge = item.badge === "notif" ? notifBadge : item.badge === "complaints" ? complaintsBadge : 0;
            return (
              <button key={item.key} onClick={() => { setTab(item.key); onClose(); }}
                      data-testid={`portal-nav-${item.key}`}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition
                                  ${active ? "bg-[#1D4ED8] text-white shadow-sm" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"}`}>
                <Icon size={18} weight={active ? "fill" : "regular"} className="shrink-0" />
                <span className="flex-1 text-left">{item.label}</span>
                {badge > 0 && (
                  <span className={`text-[10px] font-bold rounded-full px-1.5 py-0.5 min-w-[18px] text-center
                                    ${active ? "bg-white text-[#1D4ED8]" : "bg-red-500 text-white"}`}
                        data-testid={`portal-nav-${item.key}-badge`}>{badge}</span>
                )}
              </button>
            );
          })}
          <button onClick={logout} data-testid="portal-nav-logout"
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-slate-600 hover:bg-red-50 hover:text-red-700 transition">
            <SignOut size={18} />
            <span className="text-left">Logout</span>
          </button>
        </nav>
        <div className="px-4 py-4 border-t border-slate-100 mx-3 mb-4 mt-2 bg-slate-50 rounded-xl">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-full bg-[#1D4ED8]/10 text-[#1D4ED8] grid place-items-center">
              <Headset size={16} weight="fill" />
            </div>
            <div>
              <div className="text-xs font-semibold text-slate-900">Need Help?</div>
              <div className="text-[10px] text-slate-500">Our support team is here to help you.</div>
            </div>
          </div>
          <button onClick={() => setTab("support")}
                  className="w-full text-xs font-medium text-white bg-[#1D4ED8] hover:bg-[#1e40af] rounded-lg py-2 transition"
                  data-testid="portal-sidebar-support">
            Contact Support
          </button>
        </div>
        <div className="px-5 pb-4 text-[10px] text-slate-400 leading-tight">
          © 2026 KGS Technology Group, Inc.<br/>All rights reserved.
        </div>
      </aside>
    </>
  );
}

// ── Top bar ───────────────────────────────────────────────────────
function TopBar({ title, me, initials, notifUnread, onNotifClick, onMenu, onLogout }) {
  const [userMenu, setUserMenu] = useState(false);
  return (
    <header className="bg-white border-b border-slate-200 px-4 md:px-8 py-4 flex items-center justify-between sticky top-0 z-30">
      <div className="flex items-center gap-3">
        <button onClick={onMenu} className="lg:hidden text-slate-600" data-testid="portal-menu-btn">
          <List size={22} />
        </button>
        <h1 className="text-lg font-semibold text-slate-900" data-testid="portal-page-title">{title}</h1>
      </div>
      <div className="flex items-center gap-4">
        <button onClick={onNotifClick} className="relative text-slate-600 hover:text-slate-900 transition"
                data-testid="portal-topbar-bell">
          <Bell size={22} weight="regular" />
          {notifUnread > 0 && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full text-[9px] w-4 h-4 grid place-items-center font-bold" data-testid="portal-topbar-bell-badge">
              {notifUnread > 9 ? "9+" : notifUnread}
            </span>
          )}
        </button>
        <div className="relative">
          <button onClick={() => setUserMenu((v) => !v)}
                  data-testid="portal-user-menu"
                  className="flex items-center gap-2 hover:bg-slate-50 rounded-lg px-2 py-1 transition">
            <span className="w-9 h-9 rounded-full bg-gradient-to-br from-[#F5B800] to-[#e6a800] text-slate-900 grid place-items-center text-xs font-bold shadow-sm">
              {initials || "KG"}
            </span>
            <div className="hidden md:block text-left">
              <div className="text-sm font-medium text-slate-900 leading-tight">{me.full_name}</div>
            </div>
            <CaretDown size={12} className="text-slate-400 hidden md:block" />
          </button>
          {userMenu && (
            <div className="absolute right-0 mt-2 w-56 bg-white border border-slate-200 rounded-xl shadow-lg py-1.5 z-40">
              <div className="px-3 py-2 border-b border-slate-100">
                <div className="text-sm font-medium text-slate-900">{me.full_name}</div>
                <div className="text-xs text-slate-500 truncate">{me.email}</div>
              </div>
              <button onClick={() => { setUserMenu(false); onLogout(); }}
                      className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                      data-testid="portal-user-menu-logout">
                <SignOut size={14} /> Sign out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

// ── Dashboard home ────────────────────────────────────────────────
function DashboardHome({ me, firstName, dashboard, onNav, ivs, subs }) {
  const pct = dashboard?.profile_completion_pct ?? 0;
  const dossier = me.dossier || {};
  const p = dossier.personal_info || {};
  const has = (v) => v != null && v !== "" && !(Array.isArray(v) && v.length === 0);
  const sections = [
    { key: "personal", label: "Personal Info", icon: User,
      status: has(p.first_name) && has(p.last_name) && has(p.phone_number || me.phone) ? "Completed"
              : (has(p.first_name) || has(p.last_name)) ? "In Progress" : "Pending" },
    { key: "education", label: "Education", icon: GraduationCap,
      status: (dossier.education || []).length > 0 ? "Completed" : "Pending" },
    { key: "experience", label: "Experience", icon: Briefcase,
      status: (dossier.work_experience || []).length > 0 ? "Completed" : "Pending" },
    { key: "skills", label: "Skills", icon: Sparkle,
      status: (dossier.skills || me.skills || []).length >= 3 ? "Completed"
              : (dossier.skills || me.skills || []).length > 0 ? "In Progress" : "Pending" },
    { key: "documents", label: "Documents", icon: FileText,
      status: has(dossier.resume_url || me.resume_url) ? "Completed" : "In Progress" },
    { key: "review", label: "Review & Submit", icon: PaperPlaneTilt,
      status: me.dossier_submitted_at ? "Completed" : "Pending" },
  ];

  const cards = [
    { key: "eval", label: "Evaluation Status",
      value: dashboard?.evaluation_status || "Pending",
      hint: dashboard?.latest_evaluation_at
            ? `Submitted on ${(dashboard.latest_evaluation_at || "").slice(0, 10)}`
            : "Complete evaluation to progress",
      icon: ClipboardText, tint: "sky" },
    { key: "mr", label: "Marketing Status",
      value: dashboard?.marketing_ready ? "Ready" : "Not Ready",
      hint: dashboard?.marketing_ready ? "You are marketing ready" : "Complete evaluation to become marketing ready",
      icon: Flag, tint: dashboard?.marketing_ready ? "emerald" : "amber" },
    { key: "apps", label: "Active Applications",
      value: String((subs || []).length),
      hint: "View your active job applications",
      icon: Briefcase, tint: "violet", onClick: () => onNav("applications") },
    { key: "iv", label: "Upcoming Interviews",
      value: String((ivs || []).filter((i) => ["Scheduled", "Rescheduled"].includes(i.status)).length),
      hint: (ivs || []).length ? `Next interview` : "None scheduled",
      icon: CalendarBlank, tint: "rose", onClick: () => onNav("applications") },
  ];

  return (
    <div className="space-y-6" data-testid="portal-dashboard-home">
      {/* Welcome hero */}
      <div className="bg-white border border-slate-200 rounded-2xl p-6 flex items-center justify-between gap-6 flex-wrap shadow-sm">
        <div>
          <h2 className="text-2xl md:text-3xl font-semibold text-slate-900">
            Welcome back, {firstName}! <span className="inline-block">👋</span>
          </h2>
          <p className="text-sm text-slate-500 mt-1">Complete your profile and get closer to your next opportunity.</p>
        </div>
        <button onClick={() => onNav("dossier")}
                data-testid="dash-continue-dossier"
                className="bg-[#1D4ED8] hover:bg-[#1e40af] text-white font-medium text-sm rounded-lg px-5 py-2.5 shadow-sm transition inline-flex items-center gap-2">
          {me.dossier_submitted_at ? "View My Dossier" : "Continue My Dossier"} <ArrowRight size={14} />
        </button>
      </div>

      {/* Profile completion */}
      <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm" data-testid="dash-profile-completion">
        <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
          <div>
            <div className="text-sm font-medium text-slate-900">Profile Completion</div>
            <div className="text-3xl font-bold text-[#1D4ED8] tabular-nums mt-0.5" data-testid="dash-completion-pct">{pct}%</div>
          </div>
          <div className="text-xs text-slate-400 tabular-nums">/ 100%</div>
        </div>
        <div className="h-2 bg-slate-100 rounded-full overflow-hidden mb-5">
          <div className="h-full bg-[#1D4ED8] rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {sections.map((s) => {
            const Icon = s.icon;
            const tone = s.status === "Completed" ? "emerald" : s.status === "In Progress" ? "amber" : "slate";
            const toneMap = {
              emerald: { pill: "text-emerald-700", bg: "bg-emerald-100" },
              amber:   { pill: "text-amber-700",   bg: "bg-amber-100" },
              slate:   { pill: "text-slate-500",   bg: "bg-slate-100" },
            }[tone];
            return (
              <div key={s.key} className="flex items-center gap-2.5" data-testid={`dash-section-${s.key}`}>
                <div className={`w-9 h-9 rounded-lg ${toneMap.bg} grid place-items-center ${toneMap.pill} shrink-0`}>
                  <Icon size={16} weight="regular" />
                </div>
                <div className="min-w-0">
                  <div className="text-xs font-semibold text-slate-900 truncate">{s.label}</div>
                  <div className={`text-[10px] font-medium ${toneMap.pill}`}>{s.status}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* 4 status cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {cards.map((c) => {
          const Icon = c.icon;
          const tintMap = {
            sky: "bg-sky-100 text-sky-700",
            emerald: "bg-emerald-100 text-emerald-700",
            amber: "bg-amber-100 text-amber-700",
            violet: "bg-violet-100 text-violet-700",
            rose: "bg-rose-100 text-rose-700",
          };
          return (
            <button key={c.key} onClick={c.onClick || (() => {})}
                    data-testid={`dash-card-${c.key}`}
                    className="bg-white border border-slate-200 rounded-2xl p-5 text-left hover:shadow-md hover:-translate-y-0.5 transition-all shadow-sm">
              <div className="flex items-start gap-3">
                <div className={`w-12 h-12 rounded-xl ${tintMap[c.tint]} grid place-items-center shrink-0`}>
                  <Icon size={22} weight="regular" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[11px] font-medium text-slate-500 uppercase tracking-wider">{c.label}</div>
                  <div className="text-lg font-bold text-slate-900 mt-0.5">{c.value}</div>
                  <div className="text-xs text-slate-500 mt-1 leading-snug">{c.hint}</div>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* My Dossier section list */}
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm" data-testid="dash-dossier-list">
        <div className="px-6 py-4 border-b border-slate-100">
          <h3 className="text-base font-semibold text-slate-900">My Dossier</h3>
        </div>
        <ul>
          {[
            { icon: User, label: "Personal Information", desc: "Basic personal details", status: sections[0].status },
            { icon: ChatCircle, label: "Contact Information", desc: "Phone, email and other contact details",
              status: has(p.phone_number || me.phone) && has(me.email) ? "Completed" : "In Progress" },
            { icon: MapPin, label: "Address Information", desc: "Current address details",
              status: has(p.street_address) && has(p.current_city) && has(p.zip_code) ? "Completed" : "In Progress" },
            { icon: GraduationCap, label: "Education", desc: "Academic background", status: sections[1].status },
            { icon: Briefcase, label: "Work Experience", desc: "Professional experience", status: sections[2].status },
            { icon: Sparkle, label: "Skills", desc: "Technical and professional skills", status: sections[3].status },
            { icon: ShieldCheck, label: "Visa Information", desc: "Visa status and work authorization",
              status: has((dossier.visa_info || {}).visa_type || me.visa_status) ? "Completed" : "In Progress" },
            { icon: FileText, label: "Documents", desc: "Upload your documents", status: sections[4].status },
            { icon: PaperPlaneTilt, label: "Review & Submit", desc: "Review and submit your dossier", status: sections[5].status },
          ].map((row, i) => {
            const Icon = row.icon;
            const toneMap = {
              Completed: { pill: "bg-emerald-100 text-emerald-800", btn: null },
              "In Progress": { pill: "bg-amber-100 text-amber-800", btn: "Continue" },
              Pending: { pill: "bg-slate-100 text-slate-600", btn: "Start" },
            }[row.status];
            return (
              <li key={`dossier-${row.label}`} className={`px-6 py-3 flex items-center gap-4 ${i !== 0 ? "border-t border-slate-100" : ""}`}
                  data-testid={`dossier-row-${row.label.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="w-9 h-9 rounded-lg bg-slate-50 border border-slate-100 text-slate-500 grid place-items-center shrink-0">
                  <Icon size={16} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-900">{row.label}</div>
                  <div className="text-xs text-slate-500">{row.desc}</div>
                </div>
                <span className={`text-[11px] font-semibold rounded-full px-2.5 py-1 ${toneMap.pill}`}>{row.status}</span>
                <button onClick={() => onNav("dossier")}
                        className="text-sm text-[#1D4ED8] hover:text-[#1e40af] font-medium flex items-center gap-1 min-w-[70px] justify-end">
                  {toneMap.btn ? toneMap.btn : "View"} <ArrowRight size={12} />
                </button>
              </li>
            );
          })}
        </ul>
        <div className="bg-slate-50 border-t border-slate-100 px-6 py-3 text-xs text-slate-500 inline-flex items-center gap-2">
          <Info size={12} /> Save draft anytime and return later. Your progress is automatically saved.
        </div>
      </div>
    </div>
  );
}

// ── Right rail ───────────────────────────────────────────────────
function RightRail({ notifs, docs, onNav }) {
  return (
    <div className="p-6 space-y-6">
      <RightRailBlock title="Recent Notifications" cta="View All" onCta={() => onNav("notifications")}>
        {notifs.slice(0, 4).map((n) => {
          const Icon = n.kind?.includes("approved") || n.kind === "evaluation_completed" ? CheckCircle
                     : n.kind?.includes("rejected") ? Flag
                     : n.kind?.includes("clarification") ? ChatCircle : Info;
          const tone = n.kind?.includes("approved") || n.kind === "evaluation_completed" ? "emerald"
                     : n.kind?.includes("rejected") ? "red"
                     : n.kind?.includes("clarification") ? "amber" : "sky";
          const toneMap = {
            emerald: "text-emerald-600", red: "text-red-600",
            amber: "text-amber-600", sky: "text-sky-600",
          }[tone];
          return (
            <div key={n.id} className="flex items-start gap-3">
              <Icon size={16} weight="fill" className={`shrink-0 mt-0.5 ${toneMap}`} />
              <div className="min-w-0">
                <div className="text-xs text-slate-800 leading-snug">{n.title}</div>
                <div className="text-[10px] text-slate-400 mt-0.5">{(n.created_at || "").slice(0, 16).replace("T", " · ")}</div>
              </div>
            </div>
          );
        })}
        {notifs.length === 0 && <div className="text-xs text-slate-400 italic">You're all caught up.</div>}
      </RightRailBlock>

      <RightRailBlock title="My Documents" cta="View All" onCta={() => onNav("documents")}>
        {docs.slice(0, 4).map((d) => (
          <div key={d.id} className="flex items-center gap-3">
            <div className="w-9 h-11 rounded bg-red-50 border border-red-100 text-red-600 grid place-items-center shrink-0">
              <FilePdf size={16} weight="fill" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-xs font-medium text-slate-800 truncate">{d.filename}</div>
              <div className="text-[10px] text-slate-400">
                {d.uploaded_at ? `Uploaded on ${(d.uploaded_at || "").slice(0, 10)}` : "External link"}
              </div>
            </div>
          </div>
        ))}
        {docs.length === 0 && <div className="text-xs text-slate-400 italic">No documents yet.</div>}
      </RightRailBlock>

      <RightRailBlock title="Quick Links">
        {[
          { l: "Update Profile", to: "change-requests", slug: "update-profile" },
          { l: "Browse Jobs", to: "applications", slug: "browse-jobs" },
          { l: "Interview Schedule", to: "applications", slug: "interview-schedule" },
          { l: "Contact Support", to: "support", slug: "contact-support" },
        ].map((q) => (
          <button key={q.slug} onClick={() => onNav(q.to)}
                  className="w-full flex items-center justify-between text-sm text-slate-700 hover:text-[#1D4ED8] py-1.5 transition"
                  data-testid={`quick-link-${q.slug}`}>
            <span>{q.l}</span>
            <ArrowRight size={12} className="text-slate-400" />
          </button>
        ))}
      </RightRailBlock>
    </div>
  );
}
function RightRailBlock({ title, children, cta, onCta }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm font-semibold text-slate-900">{title}</div>
        {cta && (
          <button onClick={onCta} className="text-xs text-[#1D4ED8] hover:text-[#1e40af] font-medium">{cta}</button>
        )}
      </div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

// ── Docs / evals / apps / CRs / support / settings views ─────────
function DocumentsView({ docs }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm" data-testid="portal-documents-view">
      <h3 className="text-base font-semibold text-slate-900 mb-4">My Documents</h3>
      {docs.length === 0 && (
        <div className="text-sm text-slate-500 py-8 text-center border border-dashed border-slate-200 rounded-lg">
          No documents attached yet. Evaluation forms and uploaded links will appear here.
        </div>
      )}
      <ul className="divide-y divide-slate-100">
        {docs.map((d) => (
          <li key={d.id} className="py-3 flex items-center gap-4" data-testid={`doc-row-${d.id}`}>
            <div className="w-10 h-12 rounded bg-red-50 border border-red-100 text-red-600 grid place-items-center shrink-0">
              <FilePdf size={18} weight="fill" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-slate-900 truncate">{d.filename}</div>
              <div className="text-xs text-slate-500">
                {d.kind === "external_link" ? "External link" : `${(d.size || 0) / 1024 | 0} KB`}
                {d.uploaded_at ? ` · ${(d.uploaded_at || "").slice(0, 10)}` : ""}
              </div>
            </div>
            <a href={d.url || `${process.env.REACT_APP_BACKEND_URL}${d.download_url}`}
               target="_blank" rel="noreferrer"
               className="text-sm text-[#1D4ED8] hover:underline font-medium">Open →</a>
          </li>
        ))}
      </ul>
    </div>
  );
}

function EvaluationsView({ evals }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm" data-testid="portal-evaluations-view">
      <h3 className="text-base font-semibold text-slate-900 mb-4">My Evaluations</h3>
      {evals.length === 0 && (
        <div className="text-sm text-slate-500 py-8 text-center border border-dashed border-slate-200 rounded-lg">
          Your Group Lead's evaluations will appear here once completed.
        </div>
      )}
      <ul className="space-y-3">
        {evals.map((e) => (
          <li key={e.id} className="border border-slate-200 rounded-xl p-4" data-testid={`eval-row-${e.id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <div className="text-sm font-semibold text-slate-900 inline-flex items-center gap-2">
                  <Trophy size={14} weight="fill" className="text-amber-500" />
                  {e.version || "Evaluation"}
                  {e.marks_marketing_ready && (
                    <span className="text-[10px] bg-emerald-100 text-emerald-800 rounded-full px-2 py-0.5 font-semibold">Marketing Ready</span>
                  )}
                </div>
                <div className="text-xs text-slate-500 mt-0.5">Completed {(e.completed_at || "").slice(0, 10)} · {e.status}</div>
              </div>
              {e.scores && (
                <div className="flex items-center gap-3 text-xs">
                  {e.scores.technical != null && <ScorePill label="Tech" val={e.scores.technical} />}
                  {e.scores.communication != null && <ScorePill label="Comm" val={e.scores.communication} />}
                  {e.scores.marketing_readiness != null && <ScorePill label="MR" val={e.scores.marketing_readiness} />}
                </div>
              )}
            </div>
            {e.evaluator_summary && (
              <div className="text-xs text-slate-600 mt-2 pt-2 border-t border-slate-100 leading-relaxed">{e.evaluator_summary}</div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
function ScorePill({ label, val }) {
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-lg px-2 py-1 tabular-nums">
      <span className="text-[10px] text-slate-500 mr-1">{label}</span>
      <span className="font-semibold text-slate-800">{val}/10</span>
    </div>
  );
}

function ApplicationsView({ applications, authFetch }) {
  const viewJd = async (aid) => {
    try {
      const r = await authFetch(`/api/portal/applications/${aid}/jd`);
      const data = await r.json();
      const jd = (data && data.snapshot && data.snapshot.jd_text) || "";
      const win = window.open("", "_blank", "noopener,noreferrer");
      if (win) {
        // XSS-safe: build the <pre> element with DOM APIs and set the JD via
        // textContent so the browser never parses the string as HTML.
        win.document.title = "Job Description snapshot";
        const pre = win.document.createElement("pre");
        Object.assign(pre.style, {
          whiteSpace: "pre-wrap", fontFamily: "sans-serif",
          padding: "24px", maxWidth: "800px", margin: "auto", lineHeight: "1.5",
        });
        pre.textContent = jd;
        win.document.body.appendChild(pre);
      }
    } catch { toast.error("JD snapshot unavailable"); }
  };
  const statusPill = (s) => {
    const map = {
      submitted: { l: "Submitted", c: "bg-emerald-100 text-emerald-800" },
      started:   { l: "In Progress", c: "bg-amber-100 text-amber-800" },
    };
    const m = map[s] || { l: s, c: "bg-slate-100 text-slate-700" };
    return <span className={`text-[11px] font-semibold rounded-full px-2.5 py-1 ${m.c}`}>{m.l}</span>;
  };
  return (
    <div className="space-y-4" data-testid="portal-applications-view">
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-900">My Applications</h3>
          <span className="text-[11px] text-slate-500">Job Description preserved as at submission time</span>
        </div>
        {applications.length === 0 ? (
          <div className="text-sm text-slate-400 italic px-6 py-10 text-center" data-testid="portal-apps-empty">
            No applications yet. Your Group Lead / SDE will apply on your behalf.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500">
              <tr>
                <th className="text-left px-4 py-2.5">Job</th>
                <th className="text-left px-4 py-2.5">Company</th>
                <th className="text-left px-4 py-2.5">Location</th>
                <th className="text-left px-4 py-2.5">Match</th>
                <th className="text-left px-4 py-2.5">Submitted</th>
                <th className="text-left px-4 py-2.5">Status</th>
                <th className="text-right px-4 py-2.5">JD</th>
              </tr>
            </thead>
            <tbody>
              {applications.map((a) => (
                <tr key={a.id} className="border-t border-slate-100" data-testid={`portal-app-row-${a.id}`}>
                  <td className="px-4 py-2.5 text-slate-800 font-medium">{a.job_title || "—"}</td>
                  <td className="px-4 py-2.5 text-slate-600">{a.company || "—"}</td>
                  <td className="px-4 py-2.5 text-slate-600">{a.location || "—"}</td>
                  <td className="px-4 py-2.5 text-slate-800">
                    {a.match_summary?.overall_score != null ? `${a.match_summary.overall_score}%` : "—"}
                    {a.match_summary?.band && <div className="text-[10px] text-slate-500">{a.match_summary.band}</div>}
                  </td>
                  <td className="px-4 py-2.5 text-xs text-slate-600">
                    {a.submitted_at ? new Date(a.submitted_at).toLocaleDateString() : (a.started_at ? new Date(a.started_at).toLocaleDateString() : "—")}
                  </td>
                  <td className="px-4 py-2.5">{statusPill(a.status)}</td>
                  <td className="px-4 py-2.5 text-right">
                    {a.jd_snapshot_available ? (
                      <button onClick={() => viewJd(a.id)}
                               data-testid={`portal-app-view-jd-${a.id}`}
                               className="text-xs px-2.5 py-1 rounded border border-slate-200 hover:bg-slate-50">
                        View JD
                      </button>
                    ) : <span className="text-xs text-slate-400">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
function AppTable({ title, rows, cols, empty, testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm" data-testid={testid}>
      <div className="px-6 py-4 border-b border-slate-100">
        <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
      </div>
      {rows.length === 0 ? (
        <div className="text-sm text-slate-400 italic px-6 py-8 text-center">{empty}</div>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500">
            <tr>{cols.map((c) => <th key={c.k} className="text-left px-4 py-2.5">{c.l}</th>)}</tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.id || i} className="border-t border-slate-100">
                {cols.map((c) => <td key={c.k} className="px-4 py-2.5 text-slate-700">{c.fmt ? c.fmt(r[c.k]) : (r[c.k] || "—")}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function ChangeRequestsView({ authFetch, me, changeRequests, onRefresh }) {
  return (
    <div className="space-y-6">
      <PortalChangeRequestForm authFetch={authFetch} me={me} onSubmitted={onRefresh} />
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm" data-testid="cr-my-history">
        <div className="px-6 py-4 border-b border-slate-100">
          <h3 className="text-sm font-semibold text-slate-900">My Change Requests</h3>
        </div>
        {changeRequests.length === 0 ? (
          <div className="text-sm text-slate-400 italic px-6 py-8 text-center">You haven't submitted any change requests yet.</div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {changeRequests.map((cr) => (
              <li key={cr.id} className="px-6 py-3 flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-900 capitalize">{cr.category}</div>
                  <div className="text-xs text-slate-500 truncate">Fields: {Object.keys(cr.fields || {}).join(", ")}</div>
                  <div className="text-[10px] text-slate-400 mt-0.5">Submitted {(cr.created_at || "").slice(0, 10)}</div>
                </div>
                <StatusPill status={cr.status} />
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
function StatusPill({ status }) {
  const map = {
    pending_review: { l: "Pending", c: "bg-amber-100 text-amber-800" },
    approved: { l: "Approved", c: "bg-emerald-100 text-emerald-800" },
    rejected: { l: "Rejected", c: "bg-red-100 text-red-800" },
    clarification_requested: { l: "Clarification", c: "bg-sky-100 text-sky-800" },
    resubmitted: { l: "Resubmitted", c: "bg-indigo-100 text-indigo-800" },
  }[status] || { l: status, c: "bg-slate-100 text-slate-700" };
  return <span className={`text-[11px] font-semibold rounded-full px-2.5 py-1 ${map.c}`}>{map.l}</span>;
}

function MessagesPlaceholder() {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center shadow-sm" data-testid="portal-messages">
      <ChatCircle size={40} className="mx-auto text-slate-300 mb-3" />
      <h3 className="text-base font-semibold text-slate-900">Messages coming soon</h3>
      <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
        Direct messaging with your Group Lead is on the roadmap. For now, use the Support tab to submit questions or complaints.
      </p>
    </div>
  );
}

function SupportPanel({ authFetch, complaints, onRefresh }) {
  const [form, setForm] = useState({ kind: "complaint", subject: "", description: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await authFetch("/api/portal/complaints", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      toast.success("Submitted — support will reach out shortly.");
      setForm({ kind: "complaint", subject: "", description: "" });
      onRefresh && onRefresh();
    } catch (ex) { toast.error(ex.message); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-6" data-testid="portal-support">
      <form onSubmit={submit} className="bg-white border border-slate-200 rounded-2xl p-6 space-y-3 shadow-sm">
        <h3 className="text-base font-semibold text-slate-900">Contact Support</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <label className="block">
            <div className="text-xs font-medium text-slate-600 mb-1">Type</div>
            <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}
                    data-testid="complaint-kind"
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm">
              <option value="complaint">Complaint</option><option value="escalation">Escalation</option>
              <option value="question">Question</option>
            </select>
          </label>
          <label className="block">
            <div className="text-xs font-medium text-slate-600 mb-1">Subject</div>
            <input required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })}
                   data-testid="complaint-subject"
                   className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" />
          </label>
        </div>
        <label className="block">
          <div className="text-xs font-medium text-slate-600 mb-1">Message</div>
          <textarea required rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                    data-testid="complaint-description"
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm resize-none" />
        </label>
        <button type="submit" disabled={busy}
                data-testid="complaint-submit"
                className="text-sm bg-[#1D4ED8] hover:bg-[#1e40af] text-white rounded-lg px-4 py-2 font-medium disabled:opacity-40 inline-flex items-center gap-1.5">
          <PaperPlaneTilt size={14} /> Submit
        </button>
      </form>

      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
        <div className="px-6 py-4 border-b border-slate-100">
          <h3 className="text-sm font-semibold text-slate-900">My Tickets</h3>
        </div>
        {complaints.length === 0 ? (
          <div className="text-sm text-slate-400 italic px-6 py-8 text-center">No support tickets yet.</div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {complaints.map((c) => (
              <li key={c.id} className="px-6 py-3 flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium text-slate-900">{c.subject}</div>
                  <div className="text-xs text-slate-500">{c.kind} · {(c.created_at || "").slice(0, 10)}</div>
                </div>
                <span className="text-[11px] font-semibold rounded-full px-2.5 py-1 bg-slate-100 text-slate-700">{c.status}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function SettingsView({ me, onLogout }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm space-y-4" data-testid="portal-settings">
      <h3 className="text-base font-semibold text-slate-900">Profile Settings</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
        <SettingsRow k="Full Name" v={me.full_name} />
        <SettingsRow k="Email" v={me.email} />
        <SettingsRow k="Phone" v={me.phone} />
        <SettingsRow k="Preferred Role" v={me.preferred_role} />
        <SettingsRow k="Placement Field" v={me.placement_field} />
        <SettingsRow k="Visa Status" v={me.visa_status} />
        <SettingsRow k="Service Start" v={me.service_start_date} />
        <SettingsRow k="Expected End" v={me.expected_end_date || me.deadline_date} />
      </div>
      {me.assigned_sde && (
        <div className="pt-4 border-t border-slate-100">
          <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">Your Service Delivery Executive</div>
          <div className="font-medium text-slate-900">{me.assigned_sde.name}</div>
          <div className="text-xs text-slate-500">{me.assigned_sde.email} · {me.assigned_sde.phone || ""}</div>
        </div>
      )}
      <div className="pt-4 border-t border-slate-100 flex items-center justify-between">
        <div className="text-xs text-slate-500">
          To update your profile, use the <span className="text-[#1D4ED8] font-medium">Change Requests</span> tab.
        </div>
        <button onClick={onLogout} data-testid="portal-settings-logout"
                className="text-sm text-red-600 hover:bg-red-50 border border-red-200 rounded-lg px-3 py-2 font-medium inline-flex items-center gap-1.5">
          <SignOut size={14} /> Sign out
        </button>
      </div>
    </div>
  );
}
function SettingsRow({ k, v }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-slate-400">{k}</div>
      <div className="text-sm text-slate-800 font-medium">{v || "—"}</div>
    </div>
  );
}

// ── Login card ────────────────────────────────────────────────────
function LoginCard({ creds, setCreds, onLogin, onStaff }) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 grid place-items-center p-4">
      <form onSubmit={onLogin} className="bg-white border border-slate-200 rounded-2xl shadow-xl p-8 w-full max-w-sm space-y-4" data-testid="portal-login">
        <div className="text-center mb-2">
          <img src={LOGO_HORIZONTAL} alt="KGS Technology Group" className="h-14 mx-auto mb-3" />
          <h2 className="text-xl font-semibold text-slate-900">Candidate Portal</h2>
          <p className="text-xs text-slate-500 mt-1">Sign in with the credentials provided by your Service Delivery team.</p>
        </div>
        <label className="block">
          <div className="text-xs font-medium text-slate-600 mb-1">Email</div>
          <input required type="email" value={creds.email} onChange={(e) => setCreds({ ...creds, email: e.target.value })}
                 data-testid="portal-email"
                 className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1D4ED8]/30 focus:border-[#1D4ED8] transition" />
        </label>
        <label className="block">
          <div className="text-xs font-medium text-slate-600 mb-1">Password</div>
          <input required type="password" value={creds.password} onChange={(e) => setCreds({ ...creds, password: e.target.value })}
                 data-testid="portal-password"
                 className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1D4ED8]/30 focus:border-[#1D4ED8] transition" />
        </label>
        <button type="submit" data-testid="portal-login-btn"
                className="w-full bg-[#1D4ED8] hover:bg-[#1e40af] text-white rounded-lg py-2.5 text-sm font-medium shadow-sm transition">
          Sign in
        </button>
        <button type="button" onClick={onStaff} className="w-full text-xs text-slate-500 hover:text-slate-700 pt-1">
          Staff login →
        </button>
      </form>
    </div>
  );
}
